import { Component, DestroyRef, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, fromEvent, map, merge, take, tap, timer } from 'rxjs';
import { AiCreditsService } from 'src/app/settings/services/ai-credits.service';

@Component({
  selector: 'ai-chatbot-credits-overlay',
  templateUrl: './ai-chatbot-credits-overlay.component.html',
  styleUrl: './ai-chatbot-credits-overlay.component.scss',
  standalone: false,
})
export class AiChatbotCreditsOverlayComponent {
  slideIn = signal(false);
  slideOut = signal(false);
  creditsUsed = toSignal(
    this.aiCreditsService.creditsUsed$.pipe(
      map((credits) => credits.usedCredits),
    ),
  );
  creditsRemaining = toSignal(
    this.aiCreditsService.creditsUsed$.pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(({ creditsRemaining, usedCredits }) => {
        if (creditsRemaining === 0 || usedCredits > 0) {
          const showOverlay = () => {
            this.slideOut.set(false);
            this.slideIn.set(true);

            if (creditsRemaining > 0) {
              this.hideOverlayAfterTimeout();
            }
          };

          if (this.isPageInForeground()) {
            showOverlay();
          } else {
            merge(
              fromEvent(window, 'focus'),
              fromEvent(document, 'visibilitychange'),
            )
              .pipe(
                filter(() => this.isPageInForeground()),
                take(1),
                takeUntilDestroyed(this.destroyRef),
              )
              .subscribe(() => showOverlay());
          }
        }
      }),
      map((credits) => credits.creditsRemaining),
    ),
  );

  constructor(
    private aiCreditsService: AiCreditsService,
    private destroyRef: DestroyRef,
  ) {
    this.aiCreditsService.closeOverlay$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.closeCreditsOverlay());
  }

  private isPageInForeground(): boolean {
    return document.hasFocus() && document.visibilityState === 'visible';
  }

  private hideOverlayAfterTimeout(): void {
    timer(10 * 1000)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeCreditsOverlay();
      });
  }

  protected closeCreditsOverlay(): void {
    this.slideIn.set(false);
    this.slideOut.set(true);
  }

  protected openCreditsModal(): void {
    this.aiCreditsService.openCreditsModal();
  }
}
